import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { BrowserRouter } from 'react-router-dom';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ENVIRONMENT } from './env.ts';
import eruda from 'eruda';
import { extractErrorMessage } from './utils/error-utils';
import { registerSW } from 'virtual:pwa-register';
import { isNative, isWeb, hydrateStorageFromNative, isIos } from './platform';
import { onBackButton } from './platform/app-lifecycle';
import { popBackHandler } from './platform/back-handler';
import { configureNativeUI } from './platform/native-ui';

/* ── Sentry: init as early as possible (STAGING/PROD only) ── */
if (import.meta.env.VITE_SENTRY_DSN && ENVIRONMENT !== 'DEV') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: ENVIRONMENT.toLowerCase(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,   // still masks password/input fields
        blockAllMedia: false,
      }),
    ],
    // Propagate trace headers to the backend so frontend→backend calls are linked
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/staging\.api\.mailander\.com/,
      /^https:\/\/api\.mailander\.com/,
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
  Sentry.setTag('service', 'app');
}

/* ── PWA: controlled update — notify React instead of force-reloading (web only) ── */
if (isWeb()) {
  registerSW({
    onNeedRefresh() {
      // Don't auto-reload. Dispatch event so AppUpdateProvider can handle it.
      window.dispatchEvent(new CustomEvent('pwa:update-available'));
    },
    onRegisteredSW(_url: string, registration: ServiceWorkerRegistration | undefined) {
      // iOS standalone doesn't check SW frequently — poll every 60 min
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
  });
}

/* ── Mobile debug console (DEV + native staging) ── */
if (ENVIRONMENT === 'DEV' || (ENVIRONMENT === 'STAGING' && isNative())) {
  eruda.init();
}

/* ── iOS PWA: force viewport recalculation ── */
if ((navigator as any).standalone) {
  requestAnimationFrame(() => {
    window.scrollTo(0, 1);
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  });
}

/* ── Disable pinch-to-zoom globally (iOS Safari ignores user-scalable=no) ── */
document.addEventListener(
  'gesturestart',
  (e) => e.preventDefault(),
  { passive: false }
);
document.addEventListener(
  'gesturechange',
  (e) => e.preventDefault(),
  { passive: false }
);
document.addEventListener(
  'gestureend',
  (e) => e.preventDefault(),
  { passive: false }
);

/* ── Global error handlers ── */
let errorNotificationShown = false;
let errorTimeout: NodeJS.Timeout | null = null;

const showGlobalError = (message: string) => {
  // Prevent multiple rapid error notifications
  if (errorNotificationShown) return;
  
  errorNotificationShown = true;
  
  // Dispatch custom event that will be caught by notification system
  window.dispatchEvent(
    new CustomEvent('app:global-error', { detail: { message } })
  );
  
  // Reset flag after 3 seconds
  if (errorTimeout) clearTimeout(errorTimeout);
  errorTimeout = setTimeout(() => {
    errorNotificationShown = false;
  }, 3000);
};

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  Sentry.captureException(event.reason);
  const message = extractErrorMessage(event.reason);
  showGlobalError(message);
  event.preventDefault();
});

// Catch uncaught errors
window.addEventListener('error', (event) => {
  console.error('Uncaught error:', event.error);
  Sentry.captureException(event.error);
  const message = extractErrorMessage(event.error);
  showGlobalError(message);
  event.preventDefault();
});

/* ── Expose PostHog session ID getter for axios interceptor (backend linking) ── */
(window as any).__ph_session_id = () => {
  try {
    return posthog.get_session_id?.();
  } catch {
    return undefined;
  }
};

/* ── Native: Android back button handler ── */
if (isNative()) {
  onBackButton(() => {
    // First try overlay stack (sidebar, settings, notes, etc.)
    if (popBackHandler()) return;
    // Otherwise fall back to browser history
    if (window.history.length > 1) {
      window.history.back();
    }
  });
}

const queryClient = new QueryClient();

const AppProviders = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </BrowserRouter>
);

export const Root = () => {
  if (ENVIRONMENT === 'DEV') {
    return <AppProviders />;
  }

  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        capture_exceptions: true,
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-ph-mask]',
        },
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
      }}
    >
      <AppProviders />
    </PostHogProvider>
  );
};

/* ── Boot: hydrate native storage before rendering, then hide splash ── */
async function boot() {
  // On iOS native, hide the inline HTML splash immediately so only
  // the native LaunchScreen.storyboard logo is visible during boot.
  if (isIos()) {
    const inlineSplash = document.getElementById('inline-splash');
    if (inlineSplash) inlineSplash.style.display = 'none';
  }

  await hydrateStorageFromNative();
  await configureNativeUI();
  createRoot(document.getElementById('root')!).render(<Root />);

  if (isNative()) {
    const { SplashScreen } = await import('@capacitor/splash-screen');

    if (isIos()) {
      // iOS LaunchScreen.storyboard already shows branding — hide with fade.
      await SplashScreen.hide({ fadeOutDuration: 300 });
    } else {
      // Android: launchShowDuration:0 skips the Capacitor overlay —
      // the window background (layer-list with bitmap logo) is visible
      // until the WebView renders the inline-splash SVG.
      await SplashScreen.hide({ fadeOutDuration: 0 });
    }
  }
}

boot();
